// ============================================================================
// PÁGINA: LegalBIDashboard.jsx — v1.0 — Passo 48 MC3
// Legal Business Intelligence: Rentabilidade Real por Processo/Cliente
// Art. 48.3 — Interface executiva de alta densidade
// ============================================================================

import React, { useState } from 'react';
import { BarChart3, Clock, TrendingDown, TrendingUp, Award, AlertTriangle, RefreshCw, ChevronUp, ChevronDown } from 'lucide-react';
import {
  useLegalBI,
  PROFITABILITY_THRESHOLD_HIGH,
  PROFITABILITY_THRESHOLD_MEDIUM,
} from '../hooks/useLegalBI';

// ── Helpers ──────────────────────────────────────────────────────────────────

const fmtBRL = (value) => {
  if (value === null || value === undefined) return '—';
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
};

const fmtHours = (h) => {
  if (!h && h !== 0) return '—';
  const total = Math.max(0, h);
  const hh = Math.floor(total);
  const mm = Math.round((total - hh) * 60);
  return `${hh}h${String(mm).padStart(2, '0')}min`;
};

const fmtRate = (rate) => {
  if (rate === null || rate === undefined) return '—';
  return `${fmtBRL(rate)}/h`;
};

// ── Cor semântica da coluna Valor/Hora ───────────────────────────────────────
const getRateColor = (rate) => {
  if (rate === null) return { bg: 'bg-gray-100', text: 'text-gray-400', label: 'Sem horas' };
  if (rate >= PROFITABILITY_THRESHOLD_HIGH)   return { bg: 'bg-emerald-50', text: 'text-emerald-700', label: 'Alta' };
  if (rate >= PROFITABILITY_THRESHOLD_MEDIUM) return { bg: 'bg-amber-50',   text: 'text-amber-700',   label: 'Média' };
  return { bg: 'bg-red-50', text: 'text-red-700', label: 'Baixa' };
};

// ── KPI Card ─────────────────────────────────────────────────────────────────
function KPICard({ icon: Icon, iconColor, label, value, sub, accent }) {
  return (
    <div className={`rounded-xl border ${accent ? 'border-[#C5A059]/40 bg-[#20243B]' : 'border-gray-200 bg-white'} px-5 py-4 flex items-start gap-4 shadow-sm`}>
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${accent ? 'bg-white/10' : 'bg-gray-100'}`}>
        <Icon className={`w-5 h-5 ${iconColor}`} />
      </div>
      <div className="min-w-0">
        <div className={`text-[10px] font-black uppercase tracking-widest mb-0.5 ${accent ? 'text-[#C5A059]' : 'text-gray-400'}`}>{label}</div>
        <div className={`text-xl font-extrabold leading-tight ${accent ? 'text-white' : 'text-[#20243B]'}`}>{value}</div>
        {sub && <div className={`text-[10px] mt-0.5 truncate max-w-[200px] ${accent ? 'text-white/50' : 'text-gray-400'}`}>{sub}</div>}
      </div>
    </div>
  );
}

// ── Legenda de Limiares ───────────────────────────────────────────────────────
function ThresholdLegend() {
  return (
    <div className="flex items-center gap-3 text-[10px] font-semibold text-gray-500 flex-wrap">
      <span className="font-black text-gray-400 uppercase tracking-widest">Limiares:</span>
      <span className="flex items-center gap-1.5">
        <span className="w-2.5 h-2.5 rounded-full bg-emerald-500 flex-shrink-0" />
        <span className="text-emerald-700">Alta ≥ {fmtBRL(PROFITABILITY_THRESHOLD_HIGH)}/h</span>
      </span>
      <span className="flex items-center gap-1.5">
        <span className="w-2.5 h-2.5 rounded-full bg-amber-400 flex-shrink-0" />
        <span className="text-amber-700">Média ≥ {fmtBRL(PROFITABILITY_THRESHOLD_MEDIUM)}/h</span>
      </span>
      <span className="flex items-center gap-1.5">
        <span className="w-2.5 h-2.5 rounded-full bg-red-400 flex-shrink-0" />
        <span className="text-red-700">Baixa {'<'} {fmtBRL(PROFITABILITY_THRESHOLD_MEDIUM)}/h</span>
      </span>
    </div>
  );
}

// ── Componente Principal ──────────────────────────────────────────────────────
export default function LegalBIDashboard() {
  const { profitability, kpis, loading, error } = useLegalBI();
  const [sortKey,  setSortKey]  = useState('ratePerHour');
  const [sortDir,  setSortDir]  = useState('desc');
  const [search,   setSearch]   = useState('');

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDir((d) => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir('desc');
    }
  };

  const SortIcon = ({ col }) => {
    if (sortKey !== col) return <ChevronUp className="w-3 h-3 text-gray-300" />;
    return sortDir === 'asc'
      ? <ChevronUp className="w-3 h-3 text-[#C5A059]" />
      : <ChevronDown className="w-3 h-3 text-[#C5A059]" />;
  };

  const displayRows = React.useMemo(() => {
    let rows = [...profitability];

    if (search.trim()) {
      const q = search.trim().toLowerCase();
      rows = rows.filter((r) => r.label.toLowerCase().includes(q) || r.key.toLowerCase().includes(q));
    }

    rows.sort((a, b) => {
      let av = a[sortKey];
      let bv = b[sortKey];
      if (av === null) av = sortDir === 'asc' ? Infinity : -Infinity;
      if (bv === null) bv = sortDir === 'asc' ? Infinity : -Infinity;
      return sortDir === 'asc' ? av - bv : bv - av;
    });

    return rows;
  }, [profitability, search, sortKey, sortDir]);

  // ── Loading ───────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="flex items-center justify-center py-24 gap-3">
        <RefreshCw className="w-5 h-5 text-[#C5A059] animate-spin" />
        <span className="text-sm font-semibold text-gray-400">Calculando rentabilidade…</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center gap-3 p-4 rounded-xl bg-red-50 border border-red-200 text-sm text-red-700">
        <AlertTriangle className="w-4 h-4 flex-shrink-0" />
        {error}
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-10">

      {/* ── Cabeçalho ──────────────────────────────────────────────────────── */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-base font-extrabold text-[#20243B] flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-[#C5A059]" />
            Legal B.I. — Rentabilidade por Processo
          </h1>
          <p className="text-[11px] text-gray-400 mt-0.5">
            Art. 48 · Cross-join Timesheet × Financial Ledger · {kpis.totalProcesses} processos/clientes analisados
          </p>
        </div>
        <ThresholdLegend />
      </div>

      {/* ── KPI Cards ──────────────────────────────────────────────────────── */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        <KPICard
          icon={BarChart3}
          iconColor="text-[#C5A059]"
          label="Valor Médio Global / Hora"
          value={kpis.globalRatePerHour !== null ? fmtRate(kpis.globalRatePerHour) : '—'}
          sub={`${fmtHours(kpis.totalHours)} trabalhadas · ${fmtBRL(kpis.totalRevenue)} faturado`}
          accent
        />
        <KPICard
          icon={TrendingUp}
          iconColor="text-emerald-600"
          label="Processo Mais Lucrativo"
          value={kpis.mostProfitable ? fmtRate(kpis.mostProfitable.ratePerHour) : '—'}
          sub={kpis.mostProfitable?.label || '—'}
        />
        <KPICard
          icon={TrendingDown}
          iconColor="text-red-500"
          label="Processo Menos Rentável"
          value={kpis.leastProfitable ? fmtRate(kpis.leastProfitable.ratePerHour) : '—'}
          sub={kpis.leastProfitable?.label || '—'}
        />
        <KPICard
          icon={Award}
          iconColor="text-amber-500"
          label="Processos Acima do Limiar"
          value={`${kpis.countAboveThreshold} / ${kpis.totalProcesses}`}
          sub={`≥ ${fmtBRL(PROFITABILITY_THRESHOLD_HIGH)}/h`}
        />
      </div>

      {/* ── Tabela de Alta Densidade ───────────────────────────────────────── */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">

        {/* Barra de filtro */}
        <div className="flex items-center gap-3 px-4 py-3 border-b border-gray-100 bg-gray-50/70">
          <div className="relative flex-1 max-w-xs">
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Filtrar por processo ou cliente…"
              className="w-full pl-3 pr-3 py-1.5 text-xs border border-gray-200 rounded-lg bg-white outline-none focus:border-[#20243B]"
            />
          </div>
          <span className="text-[10px] text-gray-400 font-semibold whitespace-nowrap">
            {displayRows.length} registro{displayRows.length !== 1 ? 's' : ''}
          </span>
        </div>

        {/* Cabeçalho da tabela */}
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-[#20243B] text-white">
                <th className="text-left px-4 py-2.5 font-extrabold uppercase tracking-widest text-[10px] w-[40%]">
                  Processo / Cliente
                </th>
                <th
                  className="text-right px-4 py-2.5 font-extrabold uppercase tracking-widest text-[10px] cursor-pointer hover:text-[#C5A059] transition whitespace-nowrap"
                  onClick={() => handleSort('revenueReal')}
                >
                  <span className="inline-flex items-center gap-1 justify-end">
                    Receita Total <SortIcon col="revenueReal" />
                  </span>
                </th>
                <th
                  className="text-right px-4 py-2.5 font-extrabold uppercase tracking-widest text-[10px] cursor-pointer hover:text-[#C5A059] transition whitespace-nowrap"
                  onClick={() => handleSort('totalHours')}
                >
                  <span className="inline-flex items-center gap-1 justify-end">
                    Horas <SortIcon col="totalHours" />
                  </span>
                </th>
                <th
                  className="text-right px-4 py-2.5 font-extrabold uppercase tracking-widest text-[10px] cursor-pointer hover:text-[#C5A059] transition whitespace-nowrap"
                  onClick={() => handleSort('billableHours')}
                >
                  <span className="inline-flex items-center gap-1 justify-end">
                    H. Cobráveis <SortIcon col="billableHours" />
                  </span>
                </th>
                <th
                  className="text-right px-4 py-2.5 font-extrabold uppercase tracking-widest text-[10px] cursor-pointer hover:text-[#C5A059] transition whitespace-nowrap"
                  onClick={() => handleSort('ratePerHour')}
                >
                  <span className="inline-flex items-center gap-1 justify-end">
                    Valor / Hora <SortIcon col="ratePerHour" />
                  </span>
                </th>
                <th className="text-center px-4 py-2.5 font-extrabold uppercase tracking-widest text-[10px] whitespace-nowrap">
                  Perfil
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {displayRows.length === 0 && (
                <tr>
                  <td colSpan={6} className="text-center py-12 text-sm text-gray-400">
                    <Clock className="w-6 h-6 text-gray-300 mx-auto mb-2" />
                    Nenhum dado para cruzar. Registre horas no Timesheet e lançamentos no Financeiro.
                  </td>
                </tr>
              )}
              {displayRows.map((row, idx) => {
                const rateColor = getRateColor(row.ratePerHour);
                return (
                  <tr
                    key={row.key}
                    className={`hover:bg-gray-50/60 transition-colors ${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50/30'}`}
                  >
                    {/* Identificador */}
                    <td className="px-4 py-2.5">
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="w-5 h-5 rounded-full bg-[#20243B]/10 text-[#20243B] text-[9px] font-black flex items-center justify-center flex-shrink-0">
                          {idx + 1}
                        </span>
                        <span className="font-semibold text-[#20243B] truncate" title={row.label}>
                          {row.label}
                        </span>
                      </div>
                    </td>

                    {/* Receita Total */}
                    <td className="px-4 py-2.5 text-right tabular-nums font-bold text-[#20243B]">
                      {row.revenueReal > 0 ? fmtBRL(row.revenueReal) : <span className="text-gray-300">—</span>}
                    </td>

                    {/* Horas Totais */}
                    <td className="px-4 py-2.5 text-right tabular-nums text-gray-600 font-semibold">
                      {row.totalHours > 0 ? fmtHours(row.totalHours) : <span className="text-gray-300">—</span>}
                    </td>

                    {/* Horas Cobráveis */}
                    <td className="px-4 py-2.5 text-right tabular-nums text-gray-600 font-semibold">
                      {row.billableHours > 0
                        ? <span className="text-emerald-700">{fmtHours(row.billableHours)}</span>
                        : <span className="text-gray-300">—</span>
                      }
                    </td>

                    {/* Valor / Hora — semântico */}
                    <td className="px-4 py-2.5 text-right">
                      <span className={`inline-block tabular-nums font-extrabold rounded px-2 py-0.5 text-[11px] ${rateColor.bg} ${rateColor.text}`}>
                        {fmtRate(row.ratePerHour)}
                      </span>
                    </td>

                    {/* Badge de Perfil */}
                    <td className="px-4 py-2.5 text-center">
                      <span className={`inline-block text-[9px] font-black uppercase tracking-wider rounded px-2 py-0.5 ${rateColor.bg} ${rateColor.text}`}>
                        {rateColor.label}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Footer */}
        {displayRows.length > 0 && (
          <div className="px-4 py-2.5 bg-gray-50/80 border-t border-gray-100 flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-5 text-[10px] text-gray-500 font-semibold">
              <span>
                Receita Total:{' '}
                <span className="text-[#20243B] font-extrabold">{fmtBRL(kpis.totalRevenue)}</span>
              </span>
              <span>
                Horas Totais:{' '}
                <span className="text-[#20243B] font-extrabold">{fmtHours(kpis.totalHours)}</span>
              </span>
            </div>
            <div className="text-[9px] text-gray-300 font-mono">
              Art. 61.1 · Proteção de Ponto Flutuante Ativa · {new Date().toLocaleDateString('pt-BR')}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
